import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Form, Input, Select, Switch, Button, Space, Spin, Alert, message } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import {
  getNotificationPreference,
  createNotificationPreference,
  updateNotificationPreference,
  type CreateNotificationPreferenceRequest,
  type UpdateNotificationPreferenceRequest,
} from '@/api/adminApi';
import { useAuth } from '@/hooks/useAuth';

const CHANNELS = ['email', 'in_app', 'sms', 'push'];
const EVENT_TYPES = ['ticket_assigned', 'deal_won', 'deal_lost', 'invoice_generated', 'subscription_expiring', 'workflow_completed'];
const FREQUENCIES = ['immediate', 'daily_digest', 'weekly_digest'];

interface NotifPrefFormValues {
  user_id: string;
  channel: string;
  event_type: string;
  is_enabled: boolean;
  frequency: string;
  quiet_hours_start?: string;
  quiet_hours_end?: string;
}

export default function NotificationPrefForm() {
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [form] = Form.useForm<NotifPrefFormValues>();

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isEdit || !id) return;
    setLoading(true);
    setError(null);
    getNotificationPreference(id)
      .then((pref) => {
        form.setFieldsValue({
          user_id: pref.user_id,
          channel: pref.channel,
          event_type: pref.event_type,
          is_enabled: pref.is_enabled,
          frequency: pref.frequency,
          quiet_hours_start: pref.quiet_hours_start ?? undefined,
          quiet_hours_end: pref.quiet_hours_end ?? undefined,
        });
      })
      .catch((err) => setError(err instanceof Error ? err.message : t('app.not_found')))
      .finally(() => setLoading(false));
  }, [isEdit, id, form, t]);

  const handleSubmit = async (values: NotifPrefFormValues) => {
    setSubmitting(true);
    setError(null);
    try {
      if (isEdit && id) {
        const dto: UpdateNotificationPreferenceRequest = {
          is_enabled: values.is_enabled,
          frequency: values.frequency,
          quiet_hours_start: values.quiet_hours_start || null,
          quiet_hours_end: values.quiet_hours_end || null,
        };
        await updateNotificationPreference(id, dto);
        message.success(t('admin.notification_pref_update_success'));
      } else {
        const dto: CreateNotificationPreferenceRequest = {
          user_id: values.user_id,
          channel: values.channel,
          event_type: values.event_type,
          is_enabled: values.is_enabled,
          frequency: values.frequency,
          quiet_hours_start: values.quiet_hours_start,
          quiet_hours_end: values.quiet_hours_end,
        };
        await createNotificationPreference(dto);
        message.success(t('admin.notification_pref_create_success'));
      }
      navigate('/admin/notification-preferences');
    } catch (err) {
      setError(err instanceof Error ? err.message : t('app.error'));
    } finally {
      setSubmitting(false);
    }
  };

  if (!hasPermission('admin', 'write')) {
    return <Alert type="error" message={t('app.error')} />;
  }

  if (loading) return <Spin size="large" />;

  return (
    <div>
      <Space style={{ marginBottom: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/admin/notification-preferences')}>
          {t('app.back')}
        </Button>
      </Space>

      <h2>{isEdit ? t('admin.edit_notification_pref_title') : t('admin.create_notification_pref_title')}</h2>

      {error && <Alert type="error" message={error} style={{ marginBottom: 16 }} />}

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{ is_enabled: true, frequency: 'immediate' }}
        style={{ maxWidth: 600 }}
      >
        <Form.Item
          name="user_id"
          label={t('admin.notification_field.user_id')}
          rules={[{ required: true, message: t('admin.validation.value_required') }]}
        >
          <Input disabled={isEdit} placeholder={t('admin.notification_field.user_id')} />
        </Form.Item>

        <Form.Item
          name="channel"
          label={t('admin.notification_field.channel')}
          rules={[{ required: true, message: t('admin.validation.value_required') }]}
        >
          <Select disabled={isEdit}>
            {CHANNELS.map((c) => (
              <Select.Option key={c} value={c}>
                {t(`admin.channel_options.${c}`)}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="event_type"
          label={t('admin.notification_field.event_type')}
          rules={[{ required: true, message: t('admin.validation.value_required') }]}
        >
          <Select disabled={isEdit}>
            {EVENT_TYPES.map((e) => (
              <Select.Option key={e} value={e}>
                {t(`admin.event_type_options.${e}`)}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item name="is_enabled" label={t('admin.notification_field.is_enabled')} valuePropName="checked">
          <Switch />
        </Form.Item>

        <Form.Item name="frequency" label={t('admin.notification_field.frequency')}>
          <Select>
            {FREQUENCIES.map((f) => (
              <Select.Option key={f} value={f}>
                {t(`admin.frequency_options.${f}`)}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item name="quiet_hours_start" label={t('admin.notification_field.quiet_hours_start')}>
          <Input placeholder="HH:mm (e.g. 22:00)" />
        </Form.Item>

        <Form.Item name="quiet_hours_end" label={t('admin.notification_field.quiet_hours_end')}>
          <Input placeholder="HH:mm (e.g. 08:00)" />
        </Form.Item>

        <Form.Item>
          <Space>
            <Button type="primary" htmlType="submit" loading={submitting}>
              {t('app.save')}
            </Button>
            <Button onClick={() => navigate('/admin/notification-preferences')}>{t('app.cancel')}</Button>
          </Space>
        </Form.Item>
      </Form>
    </div>
  );
}
